// 留白空间 CRM —— 专业版 WebSocket 通信单例
// ---------------------------------------------------------------------------
// 设计目标（相对旧实现 header.vue / infoList.vue 的内联写法）：
//   1. 全局单例：PC(header) 与移动端(client) 共用一条连接，事件分发到所有订阅者
//   2. 自动鉴权：连接后发送 {type:"cmd", data: token} 置服务端 authed
//   3. 定时拉取未读角标：再发送 {data: token}，服务端回 JSON {m1,m2,m3,total}
//   4. 指数退避重连（不再是 100ms 暴力重连），封顶 30s
//   5. 地址自推导：由 window.location 得出 wss://<同主机>/ws，天然支持 https 页面
//      （避免旧写法 ws://IP:33333 在 https 下的「混合内容」被浏览器拦截）
//
// 服务端协议见 app/command/Webchat.php：
//   cmd 分支：仅置 authed，回 -1（ack）
//   data 分支：需先鉴权，查 redis "message_<token>"，回 {m1,m2,m3,total} 或 -1
// ---------------------------------------------------------------------------
import { getToken } from './clientAuth.js'

function wsHost() {
  const h = window.location.hostname
  /* 【P199】环境中性化：原实现把「本地预览」的兜底写死为 cs8 域名
     （liubai.cs.simpleappeal.cn），导致同一份产物在 cs7 生产上仍残留 cs8 域名。
     虽然该分支仅在 hostname 为 localhost/127.0.0.1/空 时触发（生产永恒不触发），
     但为满足「多环境最小集发布」的防呆要求，改为**只用当前页面自身来源**兜底：
       - 正常访问：hostname 恒非空 ⇒ 直接返回真实域名（cs7/cs8 各自正确）
       - 本地预览：回退到 window.location.host（含端口），由本地反代 /ws 承接
     这样产物内不再出现任何环境专属域名，cs7/cs8 可共用同一个包。 */
  if (h === 'localhost' || h === '127.0.0.1' || h.indexOf('127.0.0.1') === 0 || !h) {
    return window.location.host || 'localhost'
  }
  return h
}

function buildWsUrl() {
  const proto = window.location.protocol === 'https:' ? 'wss://' : 'ws://'
  return proto + wsHost() + '/ws'
}

const authFrame = () => JSON.stringify({ type: 'cmd', data: getToken() || '' })
const fetchFrame = () => JSON.stringify({ data: getToken() || '' })

class CrmWebSocket {
  constructor() {
    this.socket = null
    this._connecting = false
    this._closedByUser = false
    this.retryCount = 0
    this.baseDelay = 1000
    this.maxDelay = 30000
    this.interval = 5000 // 鉴权保活 + 拉取未读角标
    this._timer = null
    this._reconnectTimer = null
    this._stableTimer = null
    this._listeners = Object.create(null) // event -> Set<fn>
    this._statusListeners = new Set()
    this._connected = false
  }

  /* ---------------- 事件订阅 ---------------- */
  on(event, fn) {
    ;(this._listeners[event] || (this._listeners[event] = new Set())).add(fn)
    return () => this.off(event, fn)
  }
  off(event, fn) {
    if (this._listeners[event]) this._listeners[event].delete(fn)
  }
  _emit(event, payload) {
    const set = this._listeners[event]
    if (set) set.forEach(fn => { try { fn(payload) } catch (e) { console.error('[ws] listener error', e) } })
  }
  onStatus(fn) {
    this._statusListeners.add(fn)
    fn(this._connected)
    return () => this._statusListeners.delete(fn)
  }
  _setStatus(connected) {
    this._connected = connected
    this._statusListeners.forEach(fn => { try { fn(connected) } catch (e) {} })
  }

  /* ---------------- 生命周期 ---------------- */
  connect() {
    if (this._connecting) return
    if (typeof WebSocket === 'undefined') return
    // 【P30】复位"用户主动关闭"标记：close() 之后再调用 connect() 应能重新建立连接，
    //   原实现不复位，导致"退出登录 → 重新登录"后 WS 永久不再连接（静默失效）。
    this._closedByUser = false
    this._open()
  }

  /**
   * 【P30】token 变化（重新登录 / 换账号）后调用：废弃旧连接并以新 token 重建。
   * 与 connect() 的区别：connect() 在连接健康时会被 _connecting 短路，
   * 而 reconnect() 总是强制重建，确保新 token 一定被送去鉴权。
   */
  reconnect() {
    this._closedByUser = false
    this._stopTimer()
    this._clearStableTimer()
    if (this._reconnectTimer) { clearTimeout(this._reconnectTimer); this._reconnectTimer = null }
    this._dropSocket()
    this._connecting = false
    this.retryCount = 0
    this._open()
  }

  _open() {
    if (this._connecting || this._closedByUser) return
    const token = getToken()
    if (!token) {
      // 尚未登录：稍后重试（登录成功后组件会再次调用 connect()）
      if (!this._reconnectTimer) {
        this._reconnectTimer = setTimeout(() => { this._reconnectTimer = null; this._open() }, 3000)
      }
      return
    }
    // 【P30】先释放上一条连接：token 轮换（重新登录）时若不关闭旧 socket，
    //   旧连接的 onclose/onmessage 仍会异步回调并改写共享状态
    //   （_stopTimer / _setStatus(false) / _scheduleReconnect），
    //   表现为"新连接刚建好就被旧连接的回调拆掉"的诡异断连。
    this._dropSocket()
    let ws
    try {
      ws = new WebSocket(buildWsUrl())
    } catch (e) {
      console.error('[ws] create failed', e)
      this._scheduleReconnect()
      return
    }
    this._connecting = true
    this.socket = ws

    ws.onopen = () => {
      if (this.socket !== ws) return // 迟到的旧连接事件：不得影响当前连接
      this._connecting = false
      // 【P31】退避计数「只在连接稳定存活后」才归零。
      //   原实现在 onopen 里无条件 retryCount=0：一旦遇到「刚连上就被对端关闭」的
      //   病态循环（如服务端多端踢人活锁），每次 onopen 都把退避清零，
      //   重连延迟恒为 baseDelay=1s → 1 次/秒的重连风暴
      //   （实测 nginx 侧同源 /ws 101 达 30~40 次/分钟、单 IP 累计 1 万+）。
      //   改为：连接需持续存活 STABLE_MS 才认定「网络已恢复」，届时才清零退避；
      //   快速失败场景下退避按 1s→2s→4s…封顶 30s 正常增长，风暴自愈。
      this._clearStableTimer()
      this._stableTimer = setTimeout(() => {
        this._stableTimer = null
        this.retryCount = 0
      }, CrmWebSocket.STABLE_MS)
      this._setStatus(true)
      this._authAndFetch() // 立即鉴权 + 拉取一次角标
      this._startTimer()
      this._emit('open')
    }
    ws.onmessage = (evt) => { if (this.socket === ws) this._handle(evt.data) }
    ws.onclose = () => {
      if (this.socket !== ws) return // 迟到的旧连接事件：忽略，避免误停当前连接的心跳
      this._connecting = false
      this._setStatus(false)
      this._stopTimer()
      this._clearStableTimer() // 【P31】未达稳定阈值的连接被关闭，退避继续增长
      this._emit('close')
      if (!this._closedByUser) this._scheduleReconnect()
    }
    ws.onerror = (err) => console.error('[ws] error', err)
  }

  /** 【P30】释放当前 socket，且不触发重连（供 close / reconnect / 重建前调用） */
  _dropSocket() {
    const s = this.socket
    this.socket = null
    if (s) { try { s.close() } catch (e) {} }
  }

  /** 【P31】清除「连接已稳定」计时器（连接关闭/主动重建时必须清，否则会误清退避） */
  _clearStableTimer() {
    if (this._stableTimer) { clearTimeout(this._stableTimer); this._stableTimer = null }
  }

  // 先发鉴权帧（服务端置 authed），紧接拉取角标帧；同一次同步发送，服务端按序处理
  _authAndFetch() {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(authFrame())
      this.socket.send(fetchFrame())
    }
  }

  _handle(raw) {
    if (raw === '-1' || raw === -1) return // 鉴权 ack 或空数据，忽略
    let data
    try { data = JSON.parse(raw) } catch (e) { return }
    if (data && (data.m1 !== undefined || data.m2 !== undefined || data.m3 !== undefined || data.total !== undefined)) {
      this._emit('unread', data)
    }
  }

  _startTimer() {
    this._stopTimer()
    this._timer = setInterval(() => this._authAndFetch(), this.interval)
  }
  _stopTimer() {
    if (this._timer) { clearInterval(this._timer); this._timer = null }
  }

  _scheduleReconnect() {
    if (this._closedByUser) return
    this.retryCount++
    const delay = Math.min(this.baseDelay * Math.pow(2, this.retryCount - 1), this.maxDelay)
    // 【P31】加 0~300ms 抖动：避免多端/多标签在同一时刻同时重连（惊群）
    const jitter = Math.floor(Math.random() * 300)
    this._reconnectTimer = setTimeout(() => { this._reconnectTimer = null; this._open() }, delay + jitter)
  }

  close() {
    this._closedByUser = true
    this._stopTimer()
    this._clearStableTimer()
    if (this._reconnectTimer) { clearTimeout(this._reconnectTimer); this._reconnectTimer = null }
    this._dropSocket()
  }

  // 主动拉取一次未读（如进入消息页时调用）
  refresh() { this._authAndFetch() }
}

// 【P31】连接需持续存活该时长（ms）才认定「网络已恢复」，届时才把重连退避归零。
//   防止「连上即被关闭」的病态循环把退避反复清零 → 1 次/秒重连风暴。
CrmWebSocket.STABLE_MS = 10000

const crmWs = new CrmWebSocket()

// 若加载时已经登录，直接建立连接（组件也可在 mounted / 登录成功后再次调用，幂等）
if (typeof window !== 'undefined' && getToken()) {
  crmWs.connect()
}

export default crmWs



// WEBPACK FOOTER //
// ./src/util/websocket.js